/**
 * B1.8 Payment feature — mock↔live API dispatcher (ADR-109).
 *
 * Runtime switch driven by {@link APP_ENV.bizApiMode}:
 *   - `mock` (default): serves deterministic in-memory provider from
 *     {@link ./payment-api-mock} (Lovable / Storybook / pre-wire sessions).
 *     Test card numbers in {@link MOCK_TEST_CARDS} drive every branch:
 *     auto-approve, requires_3ds, declined, three_ds_fails.
 *   - `live`: routes through {@link ./payment-api-http} → `bizClient` →
 *     `/api/biz/cashier/payments/*` on the backend (Iyzico/3DS bridge).
 *
 * The dispatcher is resolved at module load so React Query cache keys, the
 * frozen `PaymentIntentSnapshot` shape, and the `useStartCardPayment` /
 * `useConfirm3DS` hook signatures stay identical regardless of mode.
 * Flipping `VITE_BIZ_API_MODE=live` (or build-time
 * `import.meta.env.VITE_BIZ_API_MODE`) graduates the feature without any
 * downstream churn.
 *
 * Pure helpers (Luhn, brand detection, cash math, idempotency keys,
 * {@link MOCK_TEST_CARDS}) are re-exported from {@link ./payment-helpers} for
 * backward-compat — `features/payment/index.ts` consumers never had to know
 * the helpers moved out of `payment-api.ts`.
 *
 * Parallels the `cashier-api.ts` / `invoice-api.ts` dispatchers shipped in
 * the Phase D FE rewire.
 *
 * @see payment-api-mock.ts
 * @see payment-api-http.ts
 * @see payment-helpers.ts
 * @see backend/src/api/business/biz-payment-routes.ts
 * @see docs/decisions/business/ADR-109-business-payment-tender-engine.md
 */

import { APP_ENV } from '@/shared/config/env';

import * as mockApi from './payment-api-mock';
import * as httpApi from './payment-api-http';

import type {
  Confirm3DSRequest,
  PaymentIntentSnapshot,
  StartCardPaymentRequest,
  StartGiftCardPaymentRequest,
  StartTransferPaymentRequest,
} from './types';

const useLive = APP_ENV.bizApiMode === 'live';

// ============================================================================
// Provider-mediated flows (mode-dispatched)
// ============================================================================

export async function startCardPayment(
  req: StartCardPaymentRequest,
): Promise<PaymentIntentSnapshot> {
  return useLive ? httpApi.startCardPayment(req) : mockApi.startCardPayment(req);
}

export async function confirm3DS(
  req: Confirm3DSRequest,
): Promise<PaymentIntentSnapshot> {
  return useLive ? httpApi.confirm3DS(req) : mockApi.confirm3DS(req);
}

export async function startGiftCardPayment(
  req: StartGiftCardPaymentRequest,
): Promise<PaymentIntentSnapshot> {
  return useLive ? httpApi.startGiftCardPayment(req) : mockApi.startGiftCardPayment(req);
}

export async function startTransferPayment(
  req: StartTransferPaymentRequest,
): Promise<PaymentIntentSnapshot> {
  return useLive ? httpApi.startTransferPayment(req) : mockApi.startTransferPayment(req);
}

/**
 * Introspection helper — exposes the currently active mode so debug panels
 * and QA tooling can surface the dispatcher choice without re-reading env.
 */
export function getPaymentApiMode(): 'mock' | 'live' {
  return useLive ? 'live' : 'mock';
}

// ============================================================================
// Pure helpers (mode-agnostic, re-exported for backward-compat)
// ============================================================================

export {
  buildIdempotencyKey,
  computeCashTender,
  detectCardBrand,
  formatCardNumber,
  isExpiryValid,
  isValidLuhn,
  last4,
  normalizeCardNumber,
} from './payment-helpers';
